import { TextInput } from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { useCallback, useEffect, useMemo, useRef, memo } from "react";
import { useFormContext } from "react-hook-form";
import { useInputProps } from "src/generator/hooks/useInputProps";
import { useCopyVal } from "../../hooks/useCopyVal";
import { mapUsers } from "src/generator/utils/mapUsers";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setCurrentSignature,
  setFeature,
} from "src/redux/slices/generatorSlice";
import { setEditing } from "src/redux/slices/entrySlice";

import { Flex } from "../../../styled";
import { getHistoricalEntryUser } from "../../../utils/logbookUtils/historicalEntryUser";
import URNHistoryButton from "../../_Modals/URNHistoryModal/URNHistoryButton";
import { URN_HISTORY_OLB_FEATURE } from "../../constants";
import CustomSelectItem from "../CustomSelect/CustomSelectItem";
import useGetUsers from "../../hooks/useGetUsers";
import useReadonlyClass from "../../hooks/useReadonlyClass";
import { useCanEditEntry } from "../../hooks/useCanEditEntry";
import type { ComboboxItem } from "@mantine/core";
import { mantineRenderOption } from "src/utils/mantineRenderOption";

interface ExtraFieldProps {
  fieldName: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  disabled?: boolean;
}

const ExtraField = memo(
  ({ fieldName, value, onChange, onBlur, disabled }: ExtraFieldProps) => {
    const overrideFieldName =
      fieldName === "vhf" ? "UHF CH" : fieldName === "phone" ? "Phone" : null;
    return (
      <TextInput
        label={
          (overrideFieldName ||
            fieldName?.charAt(0).toUpperCase() + fieldName?.slice(1) ||
            "") + ":"
        }
        placeholder={`Enter ${overrideFieldName || fieldName}`}
        defaultValue={value || ""}
        onChange={event => onChange(event.currentTarget.value)}
        onBlur={event => onBlur(event.currentTarget.value)}
        disabled={disabled}
        size="sm"
      />
    );
  },
);

ExtraField.displayName = "ExtraField";

interface Props {
  value?:
    | string
    | {
        userid: string;
        extraValue?: Record<string, string>;
        [key: string]: any;
      };
  id: string;
  onchange?: string;
  cellCase?: string;
  required?: boolean;
  additionalOnChange?: any;
  roles?: any;
  name?: string;
  domId?: string;
  autopopulateId?: string;
  feature?: string;
  unit?: string;
  tag?: string;
  disabled?: boolean;
  grayed?: boolean;
  exclusive?: string;
  exclusiveUser?: string;
  extra?: string;
  additional?: {
    showContactNumbers?: boolean;
  };
  showRanks?: boolean;
  manualEntry?: boolean;
  rowIndex?: number;
  copyValTarget?: string;
  restrictRank?: string;
}

const UserSelect = ({
  value,
  id,
  onchange,
  cellCase,
  required,
  additionalOnChange,
  roles,
  name,
  domId,
  autopopulateId,
  feature,
  unit,
  tag,
  disabled,
  grayed,
  exclusive,
  exclusiveUser,
  extra,
  additional,
  showRanks,
  manualEntry,
  rowIndex,
  copyValTarget,
  restrictRank,
}: Props) => {
  const {
    ref: addToRefs,
    onChange,
    name: fieldName,
    onBlur: fieldOnBlur,
    error: fieldError,
    styles: fieldStyles,
    ...inputProps
  } = useInputProps(id, value, required, exclusive, undefined, exclusiveUser);
  const { users: defaultUsers, features } = useSelector(
    state => state.generatorSlice,
  );
  const { setValue, watch } = useFormContext();
  const dispatch = useDispatch();
  const userSelectRef = useRef<HTMLInputElement | null>(null);
  const users = useGetUsers({ unit, defaultUsers });
  const readOnlyClass = useReadonlyClass({ readonly: disabled, grayed });
  const canEditEntry = useCanEditEntry();
  const popTags = ["userrefpop", "userrefpopiom", "userrefpopname"];
  const targetRestrictedRank = (restrictRank || "").trim();

  const matchesRestrictedRank = useCallback(
    (user?: { rolename?: string; role?: string } | null) => {
      if (!targetRestrictedRank) return true;
      const userRole = (user?.rolename || user?.role || "").trim();
      return userRole.toLowerCase() === targetRestrictedRank.toLowerCase();
    },
    [targetRestrictedRank],
  );

  const { applyCopyVal } = useCopyVal();

  const extraValue = watch(`${id}-extraValue`);
  const extraFields = useMemo(() => {
    if (!extra) return [];
    return extra.split("-").map(field => field.trim());
  }, [extra]);

  useEffect(() => {
    if (domId && features?.[domId]) {
      setValue(id, features[domId]);
    }
    if (tag === "oowCheckDrop" && features?.oowCheck) {
      const shouldSetValue = Object.values(features.oowCheck).some(v => v);
      if (shouldSetValue) {
        setValue(id, "AS PER ROTA");
      } else {
        setValue(id, "");
      }
    }

    if (
      typeof value === "object" &&
      value &&
      "extraValue" in value &&
      value.extraValue &&
      !extraValue
    ) {
      setValue(`${id}-extraValue`, value.extraValue);
    }
  }, [domId, features, id, tag, value, extraValue, setValue]);

  const parsedUsers = useMemo(() => {
    const items = JSON.parse(JSON.stringify(users));
    if (
      typeof value === "string" &&
      users.findIndex((user: any) => user.userid === value) === -1 &&
      value &&
      name &&
      name.split(" ").length > 1
    ) {
      items.push(getHistoricalEntryUser(name, value));
    }
    return items;
  }, [users, value, name]);
  const handleChange = (data: string) => {
    if (onchange === "getSignature") {
      dispatch(setCurrentSignature(data));
    }
    const userData = parsedUsers.find((user: any) => user.userid === data);

    // Check if this is selecting an existing manual entry by userid
    if (
      !userData &&
      typeof inputProps.value === "object" &&
      inputProps.value?.userid === data
    ) {
      onChange(inputProps.value);
      if (additionalOnChange) additionalOnChange(data);
      if (copyValTarget) {
        applyCopyVal({ feature, targetField: copyValTarget, value: data });
      }
      return;
    }

    if (!userData && manualEntry && data) {
      if (targetRestrictedRank) return;
      // Create new manual entry
      const manualData = {
        firstname: "",
        surname: "",
        userid: "",
        ref: "",
        rolename: "",
        mobile: "",
        phoneno: "",
        manualValue: data,
        ...(extraValue &&
          Object.keys(extraValue).length > 0 && {
            extraValue: extraValue,
          }),
      };
      onChange(manualData);

      if (additionalOnChange) additionalOnChange(data);
      if (copyValTarget) {
        applyCopyVal({
          feature,
          targetField: copyValTarget,
          value: manualData.manualValue || data,
        });
      }
      return;
    }

    if (!userData) return;
    if (!matchesRestrictedRank(userData)) return;
    const contactNumber = userData.mobile || userData.phoneno || "";
    let initialExtraValue = extraValue || {};
    if (extraFields.includes("phone")) {
      initialExtraValue = {
        ...initialExtraValue,
        phone: contactNumber,
      };
      setValue(`${id}-extraValue`, initialExtraValue);
    }

    const newData = {
      firstname: userData.firstname,
      surname: userData.surname,
      userid: userData.userid,
      ref: userData.ref,
      rolename: userData.rolename,
      mobile: userData.mobile,
      phoneno: userData.phoneno,
      ...(initialExtraValue &&
        Object.keys(initialExtraValue).length > 0 && {
          extraValue: initialExtraValue,
        }),
    };
    onChange(newData);

    // Auto-populate crew member additional fields (DOB, Address, Next of Kin)
    if (cellCase === "userrefpopiom") {
      populateCrewFields(userData);
    }

    if (additionalOnChange) additionalOnChange(data);
    // if (cellCase === "userrefpop") changeCellsValue(userData);
    // if (cellCase === "userrefpopiom") changeIomValue(userData.rolename);
    // if (cellCase === "userrefpopname")
    //   changeIomValue(userData.firstname + " " + userData.surname);

    if (popTags.includes(cellCase ?? "")) {
      changeCellsValue(userData);
    }

    if (copyValTarget) {
      applyCopyVal({
        feature,
        targetField: copyValTarget,
        value: userData?.userid ?? data,
      });
    }

    if (feature === "full_name_caps") {
      const fullNameCaps =
        (userData.firstname?.toUpperCase() || "") +
        " " +
        (userData.surname?.toUpperCase() || "");
      dispatch(setFeature({ key: feature, feature: fullNameCaps }));
    }

    if (autopopulateId) {
      dispatch(setFeature({ key: autopopulateId, feature: data }));
    }
    // Handle contact number feature
    if (additional?.showContactNumbers && userData) {
      const contactNumber = userData.mobile || userData.phoneno || "";
      if (contactNumber !== features["contactNumber"]) {
        dispatch(setFeature({ key: "contactNumber", feature: contactNumber }));
      }
      // Store the selected user ID for later use when saving
      if (userData.userid !== features["contactNumberUserId"]) {
        dispatch(
          setFeature({ key: "contactNumberUserId", feature: userData.userid }),
        );
      }
    }
  };

  const handleExtraFieldChange = useCallback(
    (fieldName: string) => {
      return (fieldValue: string) => {
        if (canEditEntry) {
          dispatch(setEditing(true));
        }
      };
    },
    [canEditEntry, dispatch],
  );

  const handleExtraFieldBlur = useCallback(
    (fieldName: string) => {
      return (fieldValue: string) => {
        const currentExtraValue = extraValue || {};
        const newExtraValue = {
          ...currentExtraValue,
          [fieldName]: fieldValue,
        };
        setValue(`${id}-extraValue`, newExtraValue);

        if (typeof inputProps.value === "object" && inputProps.value) {
          const updatedValue = {
            ...inputProps.value,
            extraValue: newExtraValue,
          };
          onChange(updatedValue);
        }
      };
    },
    [extraValue, setValue, id, inputProps.value, onChange],
  );

  const changeCellsValue = useCallback(
    (userData: any) => {
      if (!userData) return;
      const rowEl =
        (cellCase &&
          document.getElementsByClassName(cellCase)[rowIndex ?? 0]
            ?.parentElement?.parentElement?.parentElement) ??
        null;
      if (!rowEl) return;

      let nameElId = "";
      if (cellCase === "userrefpopname") {
        nameElId =
          document
            .getElementsByClassName(cellCase)
            [
              rowIndex ?? 0
            ]?.parentElement?.parentElement?.nextElementSibling?.firstElementChild?.firstElementChild?.querySelector("input")
            ?.getAttribute("name") || "";
      } else {
        nameElId =
          rowEl
            .querySelector(".seamen_name > input, .nameofsea > input")
            ?.getAttribute("name") ?? "";
      }

      const roleElId = rowEl
        .querySelector(".seamen_role > input, .caponboard > input")
        ?.getAttribute("name");
      if (nameElId)
        setValue(nameElId, userData?.firstname + " " + userData?.surname);
      if (roleElId) setValue(roleElId, userData?.rolename);
      if (feature === URN_HISTORY_OLB_FEATURE) {
        // if (thirdCellId) setValue(thirdCellId, "");
      }
    },
    [setValue],
  );

  const changeIomValue = useCallback(
    (nextValue: string) => {
      if (!nextValue) return;

      const firstNextSibling =
        userSelectRef?.current?.parentElement?.parentElement?.parentElement
          ?.parentElement?.parentElement?.parentElement?.parentElement
          ?.nextElementSibling?.lastChild?.lastChild?.lastChild;

      if (!firstNextSibling || !(firstNextSibling instanceof HTMLElement))
        return;

      const firstCellId = firstNextSibling.getAttribute("name");
      if (!firstCellId) return;

      setValue(firstCellId, nextValue);
    },
    [setValue],
  );

  const populateCrewFields = useCallback(
    (userData: any) => {
      if (!userData) return;

      // Find the row containing the current select
      const rowEl =
        (cellCase &&
          document.getElementsByClassName(cellCase)[rowIndex ?? 0]
            ?.parentElement?.parentElement?.parentElement) ??
        null;

      if (!rowEl) return;
      console.log(userData);
      const setCrewField = (selector: string, value: string | undefined) => {
        if (!value) return;

        const field = rowEl.querySelector(selector);
        const fieldName = field?.getAttribute("name");
        if (fieldName) {
          setValue(fieldName, value);
        }
      };

      setCrewField(
        ".seamen_dob > input, .seamen_dob > div > textarea",
        userData.dob,
      );
      setCrewField(
        ".seamen_address > input, .seamen_address > div > textarea",
        userData.address,
      );
      setCrewField(
        ".seamen_kin > input, .seamen_kin > div > textarea",
        userData.nextOfKin,
      );
    },
    [cellCase, rowIndex, setValue],
  );

  const getData = useCallback(() => {
    const items: any[] = [];

    /* -------------------------------------------------------------------------------------------------------/
     * WARNING / DO NOT MODIFY THIS CODE WITHOUT CHECKING, TO ENSURE ITS CORRECT
     -------------------------------------------------------------------------------------------------------/
     * This ensures that if there is a user already set, that user is guaranteed to be shown regardless
     * of the users active/inactive/archived/deleted status this is a NON NEGOTIABLE and required for legal
     * purposes if you need to ever change this, you must ensure a saved user is shown on Logbooks, Summary,
     * Reports regardless of the users active/inactive/archived/deleted status.
     */
    if (
      typeof inputProps.value === "object" &&
      matchesRestrictedRank(inputProps.value as any) &&
      parsedUsers.findIndex(
        (user: any) => user.userid === inputProps.value.userid,
      ) === -1
    ) {
      items.push({ ...inputProps.value });
    }
    //* END WARNING ---------------------------------------------------------------------------------*/

    // Pass user from historical entry to user list if they are not present in actual users (VU2PD-50)

    for (const user of parsedUsers) {
      if (!matchesRestrictedRank(user)) continue;
      if (typeof value === "object" && user.userid === (value as any).userid) {
        items.push({ ...(value as any) });
      } else if (typeof value === "string" && user.userid === value) {
        items.push(user);
      } else if (!user.deleted) {
        items.push(user);
      }
    }
    return mapUsers(
      items,
      roles ?? cellCase,
      ["userrefpop", "userrefpopname"].includes(cellCase ?? "")
        ? "ref"
        : showRanks
          ? "ref - role firstname surname"
          : "ref - firstname surname",
      ["userrefpop", "userrefpopname"].includes(cellCase ?? "")
        ? "ref - firstname surname"
        : showRanks
          ? "ref - role firstname surname"
          : undefined,
      additional,
    );
  }, [
    cellCase,
    roles,
    parsedUsers,
    value,
    inputProps.value,
    additional,
    matchesRestrictedRank,
  ]);

  const dynamicData = useMemo(() => {
    let data = getData();
    if (tag === "oowCheckDrop") {
      if (
        (features["oowCheck"] &&
          Object.values(features["oowCheck"]).some(v => v)) ||
        inputProps.value === "AS PER ROTA"
      ) {
        data = [
          ...data,
          { value: "AS PER ROTA", label: "AS PER ROTA", usertype: -1 },
        ];
      }
    }
    return data;
  }, [features, tag, getData, inputProps.value]);

  const createManualEntry = useCallback(
    (inputValue: string) => {
      if (manualEntry && inputValue) {
        const manualData = {
          firstname: "",
          surname: "",
          userid: "",
          ref: "",
          rolename: "",
          mobile: "",
          phoneno: "",
          manualValue: inputValue,
          ...(extraValue &&
            Object.keys(extraValue).length > 0 && {
              extraValue: extraValue,
            }),
        };
        onChange(manualData);

        if (additionalOnChange) additionalOnChange(inputValue);
      }
    },
    [manualEntry, extraValue, onChange, additionalOnChange],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = event.currentTarget.value.trim();
      createManualEntry(inputValue);
    },
    [createManualEntry],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        const inputValue = event.currentTarget.value.trim();
        createManualEntry(inputValue);

        // Close the dropdown if a manual entry was created
        if (manualEntry && inputValue) {
          event.currentTarget.blur();
        }
      }
    },
    [createManualEntry, manualEntry],
  );

  const getClassName = useCallback(() => {
    if (popTags.includes(cellCase ?? "")) {
      return cellCase;
    }
    return undefined;
  }, [cellCase]);

  return (
    <Flex
      direction="column"
      style={{ width: "100%" }}
      className={getClassName()}>
      <Flex align="center" gap={5} style={{ width: "100%" }}>
        <Select
          searchable
          miw="250px"
          w="100%"
          name={fieldName}
          error={fieldError}
          styles={fieldStyles}
          data={dynamicData}
          ref={ref => {
            userSelectRef.current = ref;
            addToRefs(ref);
          }}
          disabled={disabled}
          renderOption={mantineRenderOption(CustomSelectItem)}
          comboboxProps={{ withinPortal: true, position: "top", zIndex: 1000 }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={
            typeof inputProps.value === "string"
              ? inputProps.value || null
              : inputProps.value?.userid ?? null
          }
          onChange={handleChange}
          className={readOnlyClass}
        />
        {feature === URN_HISTORY_OLB_FEATURE && (
          <URNHistoryButton
            urn={
              typeof inputProps.value === "string"
                ? inputProps.value
                : (inputProps.value?.ref ?? inputProps.value?.ref)
            }
            userName={
              typeof inputProps.value !== "string"
                ? (inputProps.value?.firstname ?? inputProps.value?.firstname) +
                  " " +
                  (inputProps.value?.surname ?? inputProps.value?.surname)
                : undefined
            }
          />
        )}
      </Flex>
      {extraFields.length > 0 && inputProps.value && (
        <Flex direction="column" gap={5} mt={10}>
          {extraFields.map(fieldName => (
            <ExtraField
              key={fieldName}
              fieldName={fieldName}
              value={extraValue?.[fieldName] || ""}
              onChange={handleExtraFieldChange(fieldName)}
              onBlur={handleExtraFieldBlur(fieldName)}
              disabled={disabled}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default UserSelect;